import { useEffect } from "react";
import { getSiteSettings } from "../lib/sanityQueries";

// Injects Google Tag Manager once, using the GTM ID from Sanity Site Settings.
// GTM is the single analytics entry point (GA4 is configured inside the GTM
// container), so no standalone GA4 tag is injected — avoiding double-counting.
const injectGTM = (id) => {
    if (!id) return;
    if (document.getElementById("gtm-script")) return; // inject only once

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });

    const script = document.createElement("script");
    script.id = "gtm-script";
    script.async = true;
    script.src = "https://www.googletagmanager.com/gtm.js?id=" + id;
    document.head.appendChild(script);
    // No <noscript> iframe: it only helps visitors without JavaScript, and when created
    // from JavaScript it actually loads, adding a wasted request on every page view.
};

// Start analytics after the page has finished loading and the browser is idle, so the
// tag manager does not compete with the page's own content for the main thread.
const whenIdleAfterLoad = (fn) => {
    const schedule = () =>
        "requestIdleCallback" in window ? window.requestIdleCallback(fn, { timeout: 4000 }) : setTimeout(fn, 2000);
    if (document.readyState === "complete") schedule();
    else window.addEventListener("load", schedule, { once: true });
};

// react-snap prerenders pages in a headless browser with this user agent. Third-party
// tags must not run there, or their <script> tags get baked into the static HTML.
const isPrerender = () => typeof navigator !== "undefined" && navigator.userAgent === "ReactSnap";

// Only count visits to the real site. Preview and workers.dev addresses never load analytics,
// so reviewing EP-001 work doesn't show up as website traffic.
const isLiveSite = () =>
    typeof window !== "undefined" && /(^|\.)easilinked\.com$/.test(window.location.hostname);

export const Analytics = () => {
    useEffect(() => {
        if (isPrerender() || !isLiveSite()) return undefined;
        let active = true;
        whenIdleAfterLoad(() => {
            getSiteSettings()
                .then((data) => {
                    if (active && data?.googleTagManagerId) injectGTM(data.googleTagManagerId);
                })
                .catch(() => {});
        });
        return () => {
            active = false;
        };
    }, []);

    return null;
};
